import { VonqMapping } from "../types/vonqMapping";

/** Ignore volatile vonqMapping metadata when comparing form state. */
export function stripVonqMappingMeta(
  mapping?: VonqMapping | Record<string, unknown> | null
): Record<string, unknown> {
  if (!mapping || typeof mapping !== "object") return {};
  const { syncedAt, syncSource, ...rest } = mapping as Record<string, unknown>;
  return rest;
}

export function vonqMappingsEqual(
  a?: VonqMapping | Record<string, unknown> | null,
  b?: VonqMapping | Record<string, unknown> | null
): boolean {
  return (
    JSON.stringify(stripVonqMappingMeta(a)) ===
    JSON.stringify(stripVonqMappingMeta(b))
  );
}

/** Returns true when incoming parent data matches our last emitted snapshot. */
export function isSelfEmittedFormUpdate(
  incomingSnapshot: string,
  lastEmittedSnapshotRef: { current: string | null }
): boolean {
  return (
    lastEmittedSnapshotRef.current !== null &&
    incomingSnapshot === lastEmittedSnapshotRef.current
  );
}

export function buildJobProfileFirstSnapshot(data: Record<string, unknown>): string {
  return JSON.stringify({
    jobTitle: data.jobTitle || "",
    hiringManagerId: data.hiringManagerId || "",
    workingLocation: data.workingLocation || "",
    workLocationRatio: data.workLocationRatio || "",
    vacancyReason: data.vacancyReason || "",
    contractType: data.contractType || "",
    hoursPerWeek: data.hoursPerWeek || "",
    salaryRange: data.salaryRange || "",
    weeklyHoursFrom: data.weeklyHoursFrom || "",
    weeklyHoursTo: data.weeklyHoursTo || "",
    salaryFrom: data.salaryFrom || "",
    salaryTo: data.salaryTo || "",
    salaryPeriod: data.salaryPeriod || "",
    salaryCurrency: data.salaryCurrency || "",
    benefitsWithThisRole: data.benefitsWithThisRole || "",
    vonqMapping: stripVonqMappingMeta(data.vonqMapping as VonqMapping),
  });
}

export function buildJobProfileSecondSnapshot(data: Record<string, unknown>): string {
  return JSON.stringify({
    taskOfThisPosition: data.taskOfThisPosition || "",
    anyProjectsOfCandidates: data.anyProjectsOfCandidates || "",
    educationAndExperience: data.educationAndExperience || "",
    technicalSkillsRequired: data.technicalSkillsRequired || "",
    softSkillsRequired: data.softSkillsRequired || "",
    preferredCertificationsLicenses: data.preferredCertificationsLicenses || "",
    idealCandidateProfile: data.idealCandidateProfile || "",
    idealExperienceLevel: data.idealExperienceLevel || "",
    industrySpecificExperienceRequired: data.industrySpecificExperienceRequired || "",
    vonqMapping: stripVonqMappingMeta(data.vonqMapping as VonqMapping),
  });
}

export function buildJobProfileThirdSnapshot(data: Record<string, unknown>): string {
  return JSON.stringify({
    roleChallenges: data.roleChallenges || "",
    organizationalGrowthOpportunities: data.organizationalGrowthOpportunities || "",
    positionGoals: data.positionGoals || "",
    successMetrics: data.successMetrics || "",
  });
}

export function buildJobProfileFourthSnapshot(data: Record<string, unknown>): string {
  return JSON.stringify({
    teamInteraction: data.teamInteraction || "",
    reportingStructure: data.reportingStructure || "",
    cultureFit: data.cultureFit || "",
  });
}

export function buildJobProfileFifthSnapshot(data: Record<string, unknown>): string {
  return JSON.stringify({
    positionTimeline: data.positionTimeline ?? null,
    urgentNeeds: data.urgentNeeds || "",
    interviewProcess: data.interviewProcess || [],
    additionalRequirements: data.additionalRequirements || "",
  });
}
